import { FormEvent, useEffect, useState } from 'react';
import Botpoison from '@botpoison/browser';
import { useValidationContext, ValidationMessages } from 'context/validation';
import { useRouter } from 'next/router';
import { PrismicRichText } from 'prismic/types/richtext';
import { z, ZodIssueCode } from 'zod';

import { Form } from 'components/form-elements/Form';
import { FormLayout } from 'components/form-layout/FormLayout';
import { asText, RichText } from 'components/rich-text/RichText';

import { CrmPostResult, mapValueToCrmPostResult } from 'types/crm-types';
import { flattenZodErrors } from 'utils/flattenZodErrors';
import { localeToPrismicLocale } from 'utils/i18n';
import { formDataToObject, zodToFormData } from 'utils/zodData';

import { FormError } from './FormError';
import { InferredObjectType } from './Validation';

const endpoint = '/api/form/crm';

Botpoison.init();
const botpoison = new Botpoison({
  publicKey: process.env.NEXT_PUBLIC_BOTPOSION_PUBLIC_KEY ?? '',
});

// POST payload to pages/api/crm.ts
const post = async (payload: FormData): Promise<CrmPostResult | null> => {
  let response: Response | undefined;

  try {
    response = await fetch(endpoint, {
      method: 'POST',
      body: payload,
    });
  } catch (e) {
    console.error('error submitting form', e);
    return null;
  }

  let result: CrmPostResult | null;

  // Vercel only allows 4.5MB payload and we wan't to get the error to the client
  // we validate the size before posting, but just in case we'll handle it here also
  if (!response.ok && response.status === 413) {
    const postError = new z.ZodError([
      {
        code: ZodIssueCode.custom,
        message: 'form_file_too_big',
        path: ['api'],
      },
    ]);
    return {
      success: false,
      errors: flattenZodErrors(postError),
    };
  }

  try {
    const json = await response.json();
    result = mapValueToCrmPostResult(json);
  } catch (e) {
    console.error(e);
    return null;
  }

  return result;
};

type Props = {
  formId: string;
  children: React.ReactNode;
  image?: React.ReactElement;
  title: PrismicRichText;
  content: PrismicRichText;
  successMessage: PrismicRichText;
  validation?: InferredObjectType;
};

export const FormContainer = ({
  formId,
  title,
  content,
  successMessage,
  image,
  validation,
  children,
}: Props) => {
  const { setMessages } = useValidationContext();

  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const { locale, query } = useRouter();

  // Handle form submit
  const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    setLoading(true);

    const formData = new FormData(e.currentTarget);
    const uid = Array.isArray(query.uid) ? query.uid : [query.uid ?? ''];

    formData.append('formId', formId);
    formData.append('lang', localeToPrismicLocale(locale));
    formData.append('uid', encodeURIComponent(uid.join('-')));

    // Validation object used for zod validation
    const validate = formDataToObject(formData);
    const valid = validation?.safeParse(validate);

    if (valid?.success) {
      try {
        // `valid.data` will include the preprocessed and transformed values
        const payload = zodToFormData(valid.data, formData, validate._strip);
        const { solution } = await botpoison.challenge();
        formData.append('_botpoison', solution);
        const submitResult = await post(payload);
        if (submitResult?.errors) {
          setMessages(submitResult.errors as ValidationMessages);
        } else {
          setSubmitted(true);
        }
      } catch (e) {
        console.error('unable to submit form', e);
      } finally {
        setLoading(false);
      }
    } else {
      if (validation) {
        const issues = flattenZodErrors(valid?.error);
        setMessages(issues);
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    if (submitted) {
      window.scrollTo(0, 0);
    }
  }, [submitted]);

  return (
    <FormLayout
      image={image}
      formSubmitted={submitted}
      title={asText(title)}
      content={<RichText>{content}</RichText>}
      successMessage={<RichText>{successMessage}</RichText>}
      columns
    >
      <Form encType="multipart/form-data" onSubmit={onSubmit} loading={loading}>
        {children}
        <FormError name="api" />
      </Form>
    </FormLayout>
  );
};
